import cls from 'classnames';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';

import styles from './index.module.scss';
import { postWorkspaceDraftDelete } from '../../../api';
import { postUnbindNpc } from '../../../api/npc';
import emptyImage from '../../../assets/Edit/empty.png';
import { useNormalListScroll } from '../../../hooks/useNormalList';
import {
  useAppDispatch,
  useAppSelector,
  getWorkspaceList,
  setCurrentTab,
  setToastInfo,
  setToastVisible,
  deleteNpc,
} from '../../../state';
import { track } from '../../../track';
import { WorkspaceListReqType, WorkspaceNpcInfo, WorkspaceNpcStatusInfo } from '../../../types';
import { EWorkspaceNpcStatus } from '../../../types/npc';
import Notification from '../../EditHeader/Notification';
import { LocalImage } from '../../Image';
import { Tabs } from '../../Tabs';
import { getNpcStatus, NpcList } from '../NpcList';
import { SimpleModal } from '../SimpleModal';

const PAGE = 'ugc_main_page';

const NpcPermissionModal = dynamic(() =>
  import('../').then(({ NpcPermissionModal }) => NpcPermissionModal)
);

export const ENpcStatusTrackText: Record<EWorkspaceNpcStatus, string> = {
  [EWorkspaceNpcStatus.Draft]: 'draft',
  [EWorkspaceNpcStatus.Release]: 'approved',
  [EWorkspaceNpcStatus.ReviewFailed]: 'approve_fail',
  [EWorkspaceNpcStatus.UnderReview]: 'approving',
};

export function trackWithNpc(
  npcInfo: { npc_id: number; status_info?: WorkspaceNpcStatusInfo },
  event_name: string,
  page: string,
  event_data?: Record<string, unknown>,
  method?: 'send' | 'collect'
) {
  const { npc_id, status_info } = npcInfo;
  track?.[method || 'send'](event_name, page, {
    npc_id,
    ugc_npc_approving_status: status_info
      ? ENpcStatusTrackText[getNpcStatus(status_info)]
      : undefined,
    ...(event_data || {}),
  });
}

function NpcListByType({
  type,
  openPermissionModal,
  openDeleteModal,
  defaultImg,
}: {
  type: WorkspaceListReqType;
  openPermissionModal: (npcInfo: WorkspaceNpcInfo) => void;
  openDeleteModal: (npcInfo: WorkspaceNpcInfo) => void;
  defaultImg: string;
}) {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const allRef = useRef<HTMLDivElement | null>(null);
  const { data: allNpcList, loading, hasMore } = useAppSelector((state) => state.home[type]);
  const allNpcListLoadMore = useCallback(async () => {
    await dispatch(
      getWorkspaceList({
        params: {
          req_type: type,
        },
      })
    );
  }, [dispatch, type]);

  useNormalListScroll({
    ref: allRef,
    loading,
    hasMore,
    loadingMore: allNpcListLoadMore,
  });

  const handleClickCreate = useCallback(() => {
    track.send('create_ai_click', PAGE);
    location.href = '/create/edit';
  }, []);

  if (allNpcList.length === 0 && !hasMore) {
    return (
      <div className={styles.empty}>
        <LocalImage src={emptyImage} />
        <div className={styles.emptyText}>
          <div>{`${t('home_empty_prefix')}`}</div> &nbsp;
          <div className={styles.clickableText} onClick={handleClickCreate}>
            {t('home_empty_clickable')}
          </div>{' '}
          &nbsp;
          <div>{`${t('home_empty_suffix')}`}</div>
        </div>
      </div>
    );
  }

  return (
    <NpcList
      defaultImg={defaultImg}
      npcList={allNpcList}
      scrollRef={allRef}
      openPermissionModal={openPermissionModal}
      openDeleteModal={openDeleteModal}
    />
  );
}

export const NpcTabs = ({
  defaultImg,
  createIcon,
}: {
  defaultImg: string;
  createIcon?: string;
}) => {
  const { t } = useTranslation();
  const { currentTab } = useAppSelector((state) => state.home);
  const { isToastVisible } = useAppSelector((state) => state.edit);
  const dispatch = useAppDispatch();

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedNpc, setSelectedNpc] = useState<WorkspaceNpcInfo>({} as WorkspaceNpcInfo);

  const handleOpenPermissionModal = useCallback(
    (npcInfo: WorkspaceNpcInfo) => {
      trackWithNpc(
        npcInfo,
        'ugc_npc_more_choose_click',
        PAGE,
        { ugc_npc_more_choose_type: 'permission_setting' },
        'collect'
      );
      setSelectedNpc(npcInfo);
      setShowPermissionModal(true);
    },
    [setSelectedNpc, setShowPermissionModal]
  );

  const handleOpenDeleteModal = useCallback(
    (npcInfo: WorkspaceNpcInfo) => {
      trackWithNpc(
        npcInfo,
        'ugc_npc_more_choose_click',
        PAGE,
        { ugc_npc_more_choose_type: 'delete' },
        'collect'
      );
      setSelectedNpc(npcInfo);
      setShowDeleteModal(true);
    },
    [setSelectedNpc, setShowDeleteModal]
  );

  const handleCloseNotification = useCallback(() => {
    dispatch(setToastVisible(false));
    dispatch(setToastInfo(undefined));
  }, [dispatch]);

  const handleDeleteNpc = useCallback(async () => {
    const { npc_id, status_info } = selectedNpc;
    const promise: any[] = [];

    trackWithNpc(
      selectedNpc,
      'delete_npc_popup_click',
      PAGE,
      { delete_confirm_type: 'confirm' },
      'collect'
    );

    if (status_info.last_submitted_time !== 0) {
      // 删发布
      promise.push(postUnbindNpc({ npc_id }));
    }

    // 删草稿
    promise.push(postWorkspaceDraftDelete({ npc_id }));

    await Promise.allSettled(promise);

    dispatch(deleteNpc({ npcId: npc_id }));
  }, [dispatch, selectedNpc]);

  const handleClickCreate = useCallback(() => {
    track.send('create_ai_click', PAGE);
    location.href = '/create/edit';
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    trackWithNpc(
      selectedNpc,
      'delete_npc_popup_click',
      PAGE,
      { delete_confirm_type: 'cancel' },
      'collect'
    );
    setShowDeleteModal(false);
  }, [selectedNpc]);

  const itemTest = useMemo(
    () => [
      {
        key: 'all',
        label: <span className="font-[600]">{t('all')}</span>,
        children: (
          <NpcListByType
            key={'all'}
            defaultImg={defaultImg}
            type={WorkspaceListReqType.All}
            openPermissionModal={handleOpenPermissionModal}
            openDeleteModal={handleOpenDeleteModal}
          />
        ),
      },
      {
        key: 'draft',
        label: <span className="font-[600]">{t('draft')}</span>,
        children: (
          <NpcListByType
            key={'draft'}
            defaultImg={defaultImg}
            type={WorkspaceListReqType.Draft}
            openPermissionModal={handleOpenPermissionModal}
            openDeleteModal={handleOpenDeleteModal}
          />
        ),
      },
      {
        key: 'underReview',
        label: <span className="font-[600]">{t('underReview')}</span>,
        children: (
          <NpcListByType
            key={'underReview'}
            defaultImg={defaultImg}
            type={WorkspaceListReqType.InModeration}
            openPermissionModal={handleOpenPermissionModal}
            openDeleteModal={handleOpenDeleteModal}
          />
        ),
      },
    ],
    [defaultImg, t]
  );

  return (
    <>
      <Tabs
        items={itemTest}
        defaultKey={currentTab}
        onChange={(activeKey) => dispatch(setCurrentTab(activeKey as any))}
        hiddenSplitLine
        activeLineWidth={16}
        tabBarExtraContent={
          <button
            className={cls(
              'font-medium text-[16px] border-0 px-[24px] py-[12px] rounded-[8px] cursor-pointer',
              styles.btn
            )}
            onClick={handleClickCreate}
          >
            {!!createIcon && <img src={createIcon} />}
            {t('createNpc')}
          </button>
        }
      />

      <NpcPermissionModal
        npcInfo={selectedNpc}
        show={showPermissionModal}
        onCloseModal={() => {
          setShowPermissionModal(false);
        }}
      />

      <SimpleModal
        show={showDeleteModal}
        title={
          selectedNpc.status_info &&
          getNpcStatus(selectedNpc.status_info) !== EWorkspaceNpcStatus.Draft
            ? t('unbindNpcTitle')
            : t('deleteNpcTitle')
        }
        desc={
          selectedNpc.status_info &&
          getNpcStatus(selectedNpc.status_info) !== EWorkspaceNpcStatus.Draft
            ? t('unbindNpcTitleDesc')
            : undefined
        }
        confirmText={t('confirm')}
        onClose={handleCloseDeleteModal}
        onConfirm={() => {
          handleDeleteNpc();
        }}
      />

      <Notification
        styles={styles}
        type="warning"
        open={isToastVisible}
        onClose={handleCloseNotification}
      />
    </>
  );
};
